const env = import.meta.env

interface RuntimeUrlOptions {
  runtimeOrigin?: string
  desktopRuntime?: boolean
  desktopCloudOrigin?: string
}

const windowOrigin = (): string | undefined => (
  typeof window === 'undefined' ? undefined : window.location.origin
)

const hasNativeDesktopBridge = (): boolean => (
  typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window
)

/**
 * The packaged desktop is a native shell for the customer-facing cloud
 * product, but unlike the cloud Docker image it does not receive Docker build
 * args. Keep launched cloud surfaces available in native builds while
 * retaining an explicit `false` value as a release kill switch.
 */
export function resolveDesktopCloudFeature(
  raw: string | undefined,
  desktopRuntime = hasNativeDesktopBridge(),
): boolean {
  if (raw === 'false') return false
  return raw === 'true' || desktopRuntime
}

const withoutTrailingSlash = (value: string): string => value.replace(/\/$/, '')

/**
 * Resolve an API base without ever treating the packaged Tauri asset origin as
 * the cloud control plane. In a normal browser, relative URLs remain
 * same-origin. In the desktop webview, they are rooted at the pinned Ricald
 * cloud origin instead of tauri://localhost (or http://tauri.localhost).
 */
export function resolveApiUrl(raw?: string, options: RuntimeUrlOptions = {}): string {
  const candidate = raw?.trim() || '/api'
  const runtimeOrigin = options.runtimeOrigin ?? windowOrigin()
  const desktopRuntime = options.desktopRuntime ?? hasNativeDesktopBridge()
  const desktopCloudOrigin = options.desktopCloudOrigin
    ?? env.VITE_DESKTOP_CLOUD_ORIGIN
    ?? 'https://ricald.ai'

  if (!runtimeOrigin && !desktopRuntime) return candidate

  const fallbackOrigin = desktopRuntime ? desktopCloudOrigin : runtimeOrigin
  if (!fallbackOrigin) return candidate

  try {
    const url = new URL(candidate, fallbackOrigin)
    // Docker's service name is valid inside Compose but never from a webview.
    if (url.hostname === 'backend') {
      return withoutTrailingSlash(new URL('/api', fallbackOrigin).toString())
    }
    return withoutTrailingSlash(url.toString())
  } catch {
    return withoutTrailingSlash(new URL('/api', fallbackOrigin).toString())
  }
}

/** Return a host-only ws(s) origin derived from the configured cloud API. */
export function resolveWebSocketOrigin(raw: string | undefined, apiUrl: string): string {
  const api = new URL(apiUrl, windowOrigin() ?? 'http://localhost')
  const configured = raw?.trim()
  const url = configured ? new URL(configured, api.origin) : new URL(api.origin)
  if (url.protocol === 'https:') url.protocol = 'wss:'
  if (url.protocol === 'http:') url.protocol = 'ws:'
  if (!['ws:', 'wss:'].includes(url.protocol)) {
    throw new Error('WebSocket endpoint must use ws or wss')
  }
  return `${url.protocol}//${url.host}`
}

const apiUrl = resolveApiUrl(env.VITE_API_URL)
const apiBaseUrl = resolveApiUrl(env.VITE_API_BASE_URL ?? env.VITE_API_URL)
const apiOrigin = new URL(apiBaseUrl, windowOrigin() ?? 'http://localhost').origin
const websocketOrigin = resolveWebSocketOrigin(env.VITE_WS_URL, apiBaseUrl)

export const clientConfig = {
  // API Configuration
  apiUrl,
  apiBaseUrl,
  apiOrigin,
  apiPort: env.VITE_API_PORT ?? '8000',
  wsUrl: env.VITE_WS_URL,
  websocketOrigin,

  // Authentication
  authTokenKey: env.VITE_AUTH_TOKEN_KEY ?? 'auth_token',
  googleClientId: env.VITE_GOOGLE_CLIENT_ID ?? '',

  // LLM Configuration
  llmModelType: env.VITE_LLM_MODEL_TYPE ?? 'openai',
  llmModel: env.VITE_LLM_MODEL ?? 'gpt-5.2',
  llmTemperature: Number(env.VITE_LLM_TEMPERATURE ?? 0.3),
  llmMaxTokens: Number(env.VITE_LLM_MAX_TOKENS ?? 1000),

  // Branding & White-Label Configuration
  branding: {
    productName: env.VITE_PRODUCT_NAME ?? 'Ricald',
    companyName: env.VITE_COMPANY_NAME ?? 'Ricald',
    tagline: env.VITE_TAGLINE ?? 'AI commercial operations for B2B teams',
    supportEmail: env.VITE_SUPPORT_EMAIL ?? 'support@ricald.ai',
    securityEmail: env.VITE_SECURITY_EMAIL ?? 'security@ricald.ai',
    legalEffectiveDate: env.VITE_LEGAL_EFFECTIVE_DATE ?? 'April 2026',
    privacyPolicyLastUpdated: env.VITE_PRIVACY_POLICY_LAST_UPDATED ?? 'August 11, 2026',
    docsUrl: env.VITE_DOCS_URL ?? 'https://docs.ricald.ai',
    salesEmail: env.VITE_SALES_EMAIL ?? '',
    walkthroughUrl: env.VITE_WALKTHROUGH_URL ?? '',
    statusPageUrl: env.VITE_STATUS_PAGE_URL ?? '',
    socialLinks: {
      linkedin: env.VITE_SOCIAL_LINKEDIN ?? 'https://www.linkedin.com/company/ricald-ai',
      github: env.VITE_SOCIAL_GITHUB ?? 'https://github.com',
      twitter: env.VITE_SOCIAL_TWITTER ?? 'https://x.com/Ricald_AI',
    },
  },

  // Timeouts (in milliseconds)
  timeouts: {
    api: Number(env.VITE_TIMEOUT_API ?? 30000), // 30 seconds
    websocketReconnectDelay: Number(env.VITE_TIMEOUT_WS_RECONNECT ?? 1000), // 1 second
    websocketHeartbeat: Number(env.VITE_TIMEOUT_WS_HEARTBEAT ?? 30000), // 30 seconds
    toast: Number(env.VITE_TIMEOUT_TOAST ?? 5000), // 5 seconds
    searchDebounce: Number(env.VITE_TIMEOUT_SEARCH_DEBOUNCE ?? 800), // 800ms
  },

  // Limits & Thresholds
  limits: {
    websocketMaxReconnectAttempts: Number(env.VITE_LIMIT_WS_MAX_RECONNECT ?? 5),
    searchMaxRevenue: Number(env.VITE_LIMIT_SEARCH_MAX_REVENUE ?? 1000), // millions
    searchTeamSizeMin: Number(env.VITE_LIMIT_SEARCH_TEAM_MIN ?? 1),
    searchTeamSizeMax: Number(env.VITE_LIMIT_SEARCH_TEAM_MAX ?? 10000),
  },

  // Storage Configuration (localStorage/sessionStorage keys)
  storage: {
    keyPrefix: env.VITE_STORAGE_PREFIX ?? 'ricald_ai_',
    keys: {
      authToken: env.VITE_STORAGE_KEY_AUTH_TOKEN ?? 'auth_token',
      authUser: env.VITE_STORAGE_KEY_AUTH_USER ?? 'auth_user',
      theme: env.VITE_STORAGE_KEY_THEME ?? 'theme',
    },
  },

  // Feature Flags
  features: {
    // Core features — always ON
    enableOAuth: env.VITE_FEATURE_OAUTH !== 'false',
    enableWebSocket: env.VITE_FEATURE_WEBSOCKET !== 'false',
    enableTAG: env.VITE_FEATURE_TAG !== 'false',  // ON by default (core wedge)

    // Launch feature gates — default OFF for non-wedge features
    enablePlaybooks: env.VITE_FEATURE_PLAYBOOKS === 'true',
    enableAds: env.VITE_FEATURE_ADS === 'true',
    enableEconomicAdditionality: env.VITE_FEATURE_ECONOMIC_ADDITIONALITY === 'true',
    enableNetwork: env.VITE_FEATURE_NETWORK === 'true',
    enableConversationIntelligence: env.VITE_FEATURE_CONVERSATION === 'true',
    enableDealBriefing: resolveDesktopCloudFeature(env.VITE_FEATURE_DEAL_BRIEFING),
    enableInvestorReadiness: env.VITE_FEATURE_INVESTOR_READINESS === 'true',
    enableMarketplace: env.VITE_FEATURE_MARKETPLACE === 'true',
    enableCampaigns: resolveDesktopCloudFeature(env.VITE_FEATURE_CAMPAIGNS),
    // ADR-0040 — must match NATIVE_COMMERCIAL_MODE_ENABLED on the
    // server. With the router off, the Pipeline nav item would 404.
    enableNativeCommercialMode: env.VITE_FEATURE_NATIVE_COMMERCIAL_MODE === 'true',
    // Local/internal canary for Ask Ricald's bounded durable-delegation path.
    // The backend independently requires the mission and runtime gates.
    enableDurableDelegation: env.VITE_FEATURE_DURABLE_DELEGATION === 'true',
    enableAttribution: env.VITE_FEATURE_ATTRIBUTION === 'true',
    enableAnalytics: env.VITE_FEATURE_ANALYTICS === 'true',
    enableDealIntelligence: env.VITE_FEATURE_DEAL_INTELLIGENCE === 'true',
    enableDealBriefingsSidebar: resolveDesktopCloudFeature(env.VITE_FEATURE_DEAL_BRIEFINGS_SIDEBAR),
    enableSkillBuilder: env.VITE_FEATURE_SKILL_BUILDER === 'true',
    enableNegotiation: env.VITE_FEATURE_NEGOTIATION === 'true',
    // RFC-045 v2.1 — Settings → Inbox tab visibility. Defaults OFF until
    // Postmark + MX are wired in production. Flip to true per env when
    // the channel is ready to accept real customer forwarding rules.
    enableInboxSources: resolveDesktopCloudFeature(env.VITE_FEATURE_INBOX_SOURCES),
    // The card separates official LinkedIn Ads OAuth from member-action
    // handoff. It does not authorize held LinkedIn browser automation.
    enableLinkedInMessaging: resolveDesktopCloudFeature(env.VITE_FEATURE_LINKEDIN_MESSAGING),
    // Internal Google OAuth verification checklist. It is deliberately
    // default-off and must also be restricted to a Ricald super-admin in the
    // consuming surface; customer integration settings are not a release
    // evidence dashboard.
    enableGoogleVerificationEvidence: env.VITE_FEATURE_GOOGLE_VERIFICATION_EVIDENCE === 'true',
  },
}
